import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useApiFetch, ApiError } from '../lib/api'
import { Spinner } from './Spinner'
import type { PrincipalType, PrincipalSearchItem, PrincipalSearchResponse } from '../lib/types'

export interface PrincipalSearchProps {
  type: PrincipalType
  onSelect: (item: PrincipalSearchItem) => void
  excludeIds?: string[]
  placeholder?: string
}

export function PrincipalSearch({ type, onSelect, excludeIds = [], placeholder }: PrincipalSearchProps) {
  const apiFetch = useApiFetch()
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PrincipalSearchItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const doSearch = useCallback(async (q: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ type, query: q })
      const data = await apiFetch<PrincipalSearchResponse>(`/api/v1/principals/search?${params}`)
      const filtered = data.items.filter(item => !excludeIds.includes(item.principalId))
      setResults(filtered)
      setIsOpen(true)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'cbe.throttled') {
        setError(t('principalSearch.errors.rateLimited'))
      } else if (err instanceof ApiError && err.code === 'cbe.unauthenticated') {
        setError(t('principalSearch.errors.unauthenticated'))
      } else {
        setError(t('principalSearch.errors.unavailable'))
      }
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [apiFetch, type, excludeIds, t])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value
    setQuery(q)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (q.length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }
    timerRef.current = setTimeout(() => {
      void doSearch(q)
    }, 300)
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    setQuery('')
    setResults([])
    setIsOpen(false)
  }, [type])

  function handleSelect(item: PrincipalSearchItem) {
    onSelect(item)
    setQuery('')
    setResults([])
    setIsOpen(false)
  }

  const defaultPlaceholder = type === 'User'
    ? t('principalSearch.searchUsersPlaceholder')
    : t('principalSearch.searchGroupsPlaceholder')

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          type="search"
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="principal-search-listbox"
          aria-label={type === 'User' ? t('principalSearch.searchUsersAria') : t('principalSearch.searchGroupsAria')}
          value={query}
          onChange={handleChange}
          placeholder={placeholder ?? defaultPlaceholder}
          style={{
            width: '100%',
            padding: '8px 36px 8px 12px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'inherit',
            color: 'var(--text-primary)',
            background: 'var(--bg-input)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {isLoading && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
            <Spinner size={14} />
          </div>
        )}
      </div>

      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
          {error}
        </div>
      )}

      {isOpen && !isLoading && (
        <div
          id="principal-search-listbox"
          role="listbox"
          aria-label={t('principalSearch.resultsAria')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-lg)',
            zIndex: 500,
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-tertiary)' }}>
              {t('principalSearch.noResults')}
            </div>
          ) : (
            results.map((item, idx) => (
              <button
                key={item.principalId}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => handleSelect(item)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  padding: '8px 14px',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  borderBottom: idx < results.length - 1 ? '1px solid var(--border-light)' : 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-badge)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none' }}
              >
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                  {item.principalName}
                </span>
                {item.principalEmail && (
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {item.principalEmail}
                  </span>
                )}
                {!item.principalEmail && item.principalUsername && (
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                    {item.principalUsername}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
